// MesaController.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { Mesa } from '../../../../../../../types';
import { moveTable } from '../../../../../../../../../../crud/pedidos_api/pedidos';
import { getMesasByLocalId } from '../../../../../../../../../../crud/management_api/mesas';
import { toast } from 'react-toastify';

interface ChangeTableProps {
  mesa: Mesa;
  show: boolean;
  onClose: () => void;
}

type MesaOption = { value: number; label: string; used?: boolean };

const ChangeTable: React.FC<ChangeTableProps> = ({ mesa, show, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mesas, setMesas] = useState<MesaOption[]>([]);
  const [selected, setSelected] = useState<MesaOption | null>(null);
  const [acceptChecked, setAcceptChecked] = useState(false);

  const fetchMesas = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const localId = user.local_id ?? 0;
      const resp = await getMesasByLocalId(localId);
      const opts: MesaOption[] = resp.data
        .filter((m: any) => {
          // Exclude current mesa
          if (m.mesa_id === mesa.id) return false;
          // Must be active
          if (m.state !== true) return false;
          // Exclude virtual mesas (MV) or flagged as virtual
          if (m.is_virtual === true) return false;
          // Exclude by tag containing 'MV' (case-insensitive) or 'virtual'
          const tagStr = (m.tag || '').toString().toLowerCase();
          if (tagStr.includes('mv') || tagStr.includes('virtual')) return false;
          return true;
        })
        .map((m: any) => ({
          value: m.mesa_id,
          // If mesa has a tag, prefer that as label; otherwise use mesa_number
          label: m.tag && m.tag.toString().trim() ? String(m.tag) : String(m.mesa_number),
          used: !!m.used,
        }));
      setMesas(opts);
    } catch (err) {
      console.error('Error fetching mesas:', err);
      toast.error('No se pudieron cargar las mesas');
    }
  }, [mesa.id]);

  useEffect(() => {
    if (show) {
      setIsOpen(true);
      fetchMesas();
    }
  }, [show, fetchMesas]);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  const handleSelect = (value: string | null) => {
    const mesaId = value ? Number(value) : null;
    const opt = mesas.find((m) => m.value === mesaId) ?? null;
  setSelected(opt);
  // reset acceptance checkbox when changing selection
  setAcceptChecked(false);
  };

  const handleMove = async () => {
    if (!selected) {
      toast.error('Seleccione una mesa válida');
      return;
    }
    if (selected.used && !acceptChecked) {
      toast.error('Debes aceptar la advertencia para mover a una mesa ocupada');
      return;
    }
    try {
      await moveTable(mesa.id, selected.value);
      // Use destination label directly (it may be a tag or a number)
      toast.success(`Mesa ${mesa.num} movida a ${selected.label}`);
      handleClose();
      window.location.reload();
    } catch (err) {
      console.error('Error moving table', err);
      toast.error('Error al mover la mesa');
    }
  };

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header>
        <div className="form-modal-title-container">
        <Modal.Title className="form-modal-title">Seleccione el número de mesa</Modal.Title>
        </div>
        <Button className="modal-close-button" variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mp-formgroup vertical">
          <Form.Label>Selecciona una mesa</Form.Label>

          <Dropdown onSelect={handleSelect} >
            <Dropdown.Toggle variant='none' className='mp-button light'>
                {selected ? `Mesa ${selected.label}` : 'Seleccione una mesa...'}
            </Dropdown.Toggle>

            <Dropdown.Menu>
                {mesas.map((m) => (
                  <Dropdown.Item key={m.value} eventKey={m.value.toString()} className={m.used ? 'occupied-mesa' : ''}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span>{m.label}</span>
                      {m.used && <span style={{ color: '#ffc107', fontWeight: 700 }} title="Mesa ocupada">⚠️</span>}
                    </span>
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Dropdown>
        </Form.Group>

          {/* Warning and acceptance checkbox when selected mesa is occupied */}
          {selected && selected.used && (
            <div style={{ marginTop: 12 }}>
              <div className="change-warning" style={{ background: 'rgba(255,193,7,0.08)', border: '1px solid rgba(255,193,7,0.3)', padding: '10px', borderRadius: 6 }}>
                <strong style={{ color: '#b45309' }}>Advertencia:</strong> La mesa seleccionada está ocupada. Este cambio es irreversible y puede afectar pedidos en curso.
              </div>
              <Form.Group style={{ marginTop: 8 }}>
                <Form.Check
                  type="checkbox"
                  id="accept-irreversible"
                  label="Entiendo que este cambio es irreversible"
                  checked={acceptChecked}
                  onChange={(e) => setAcceptChecked(e.target.checked)}
                />
              </Form.Group>
            </div>
          )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
        <Button variant="none" className="form-button-cancel" onClick={handleClose}>
          Cerrar
        </Button>
          <Button
            variant="primary"
            className="form-button-create"
            onClick={handleMove}
            disabled={!selected || (selected.used && !acceptChecked)}
          >
            Mover
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeTable;
